<template lang="pug">
yz-dialog(v-model="showDialog" persistent scrollable max-width="640")
  yz-card
    yz-dialog-normal-header(title="Cancelled appointments" @close="onClose")
    yz-dialog-normal-content
      .py-4.px-3
        .d-flex.align-center.mb-2
          yz-icon.mr-2(size="20" color="danger500") $yzi-alert-circle-solid
          .yz-medium-body-md.text-neutral-700 These appointments were automatically cancelled.
        .yz-regular-body-sm.text-neutral-700.mb-6 HQ has unassigned a HQ service at this location. Review each appointment below and take the necessary actions.

        div(v-if="isFetching")
          v-skeleton-loader(type="article@2")

        template(v-else)
          .d-flex.align-center.justify-space-between.mb-4(v-if="rows.length")
            yz-checkbox(:value="pageAllDone" :indeterminate="pageAnyDone && !pageAllDone" label="Select all" @input="onToggleAll")
            .yz-regular-body-sm.text-neutral-500 {{ doneCount }} of {{ totalCount }} selected

          div(v-for="(item, index) in rows" :key="item.id" :class="{ 'mt-6': index > 0 }")
            .d-flex.align-end.justify-space-between.mb-2
              .yz-heading-9.text-neutral-700 Patient {{ patientNumber(index) }}
              yz-badge.px-2.py-1(border size="sm" color="white")
                yz-checkbox(:value="!!doneMap[item.id]" label="Done" @input="onToggleDone(item.id, $event)")
            yz-card.pa-3(outlined color="#F9FAFB")
              v-row.mb-3(no-gutters align="center")
                v-col.pr-6(cols="auto" style="width: 150px;")
                  .yz-medium-body-sm.text-neutral-500 Patient details
                v-col
                  .d-flex.align-center.flex-wrap
                    yz-tooltip(:text="item.patientName")
                      .yz-medium-body-sm.text-neutral-700.text-truncate(style="max-width: 245px;") {{ item.patientName }}
                    yz-divider.mx-2(vertical v-if="item.patientPhone")
                    .yz-regular-body-sm.text-neutral-700(v-if="item.patientPhone") {{ item.patientPhone }}
                    yz-btn.ml-2(v-if="item.patientPhone" outlined, size="sm" icon="mdi-whatsapp" background-color="white" icon-size="14" @click="onWhatsapp(item)")
              v-row.mb-3(no-gutters align="center")
                v-col.pr-6(cols="auto" style="width: 150px;")
                  .yz-medium-body-sm.text-neutral-500 Doctor & Service
                v-col
                  .d-flex.align-center.flex-wrap
                    yz-tooltip(:text="item.doctor")
                      .yz-medium-body-sm.text-neutral-700.text-truncate(style="max-width: 226px;") {{ item.doctor }}
                    yz-divider.mx-2(vertical)
                    yz-tooltip(:text="item.service")
                      .yz-regular-body-sm.text-neutral-700.text-truncate(style="max-width: 226px;") {{ item.service }}
                    yz-badge.ml-2(size="sm" color="#F0F8FF" text-color="#3570CA") HQ
              v-row.mb-3(no-gutters align="center")
                v-col.pr-6(cols="auto" style="width: 150px;")
                  .yz-medium-body-sm.text-neutral-500 Date & Time
                v-col
                  .d-flex.align-center
                    .yz-medium-body-sm.text-neutral-700 {{ item.date }}
                    yz-divider.mx-2(vertical v-if="item.timeRange")
                    .yz-regular-body-sm.text-neutral-700(v-if="item.timeRange") {{ item.timeRange }}
              v-row(no-gutters align="center")
                v-col.pr-6(cols="auto" style="width: 150px;")
                  .yz-medium-body-sm.text-neutral-500 Booking fee
                v-col
                  template(v-if="item.hasBookingFee")
                    .d-flex.align-center
                      .yz-medium-body-sm.text-neutral-700 {{ item.bookingFeeAmount }}
                      .ml-2.px-2.rounded(v-if="item.paid" style="background: #ECFDF3;")
                        .yz-medium-body-xs(style="color: #027A48;") Paid
                      .ml-2.px-2.rounded(v-else style="background: #FEF3F2;")
                        .yz-medium-body-xs(style="color: #B42318;") Unpaid
                  .yz-regular-body-sm.text-neutral-500.font-italic(v-else) {{ $t('no_fees_required') }}

          .d-flex.justify-end.mt-6(v-if="totalCount > pageSize")
            yz-pagination(v-model="currentPage" :page-size.sync="pageSize" :count="totalCount" type="page" arrow @input="fetchAppointments")
    yz-dialog-normal-actions(justify="space-between")
      yz-btn(size="lg" text text-color="yz-neutral-700" label="Close" @click="onClose" :disabled="isSaving")
      .d-flex.align-center(style="gap: 12px;")
        yz-btn(size="lg" label="Update" secondary :disabled="!anyDone || isSaving" @click="onSubmit")
        yz-btn(size="lg" label="Mark as all completed" :disabled="!allDone || isSaving" @click="onSubmit")
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'CancelledAppointmentsDialog',
  props: {
    open: { type: Boolean, default: false },
    clinicSlug: { type: String, required: true }
  },
  data () {
    return {
      appointments: [],
      doneMap: {},
      isFetching: false,
      isSaving: false,
      currentPage: 1,
      pageSize: 5,
      totalCount: 0
    }
  },
  computed: {
    showDialog: {
      get () { return this.open },
      set (val) { this.$emit('update:open', val) }
    },
    rows () {
      return this.appointments.map(item => {
        const timeRange = (item.time_range && item.time_range.length === 2)
          ? `${this.$options.filters.displayTime2(item.time_range[0])} - ${this.$options.filters.displayTime2(item.time_range[1])}`
          : ''
        const hasBookingFee = item.booking_fee != null && Number(item.booking_fee) > 0
        const bookingFeeAmount = hasBookingFee
          ? this.$options.filters.displayPrice(item.booking_fee, this.$currency)
          : ''
        return {
          id: item.id,
          patientName: item.lead?.name || '',
          patientPhone: item.lead?.phone || '',
          doctor: item.provider?.name || '',
          service: item.services?.[0]?.name || '',
          date: item.start ? this.$options.filters.humanizeDate(item.start) : '',
          timeRange,
          hasBookingFee,
          bookingFeeAmount,
          paid: !!item.paid
        }
      })
    },
    doneCount () {
      return Object.values(this.doneMap).filter(Boolean).length
    },
    anyDone () {
      return this.doneCount > 0
    },
    allDone () {
      return this.totalCount > 0 && this.doneCount >= this.totalCount
    },
    pageAllDone () {
      return this.rows.length > 0 && this.rows.every(r => !!this.doneMap[r.id])
    },
    pageAnyDone () {
      return this.rows.some(r => !!this.doneMap[r.id])
    }
  },
  watch: {
    open: {
      immediate: true,
      handler (val) {
        if (val) {
          this.currentPage = 1
          this.doneMap = {}
          this.fetchAppointments()
        }
      }
    },
    pageSize () {
      this.currentPage = 1
      this.fetchAppointments()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchAppointments () {
      this.isFetching = true
      const params = {
        cancelled_by_hq: true,
        cancellation_notified: false,
        page: this.currentPage,
        page_size: this.pageSize
      }
      this.$axios.get(`/clinics/${this.clinicSlug}/appointments`, { params })
        .then(resp => {
          this.appointments = resp.data.results || []
          this.totalCount = resp.data.count || 0
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    patientNumber (index) {
      return (this.currentPage - 1) * this.pageSize + index + 1
    },
    onToggleDone (id, val) {
      this.$set(this.doneMap, id, val)
    },
    onToggleAll (val) {
      this.rows.forEach(r => this.$set(this.doneMap, r.id, !!val))
    },
    onClose () {
      this.showDialog = false
    },
    async onSubmit () {
      const ids = Object.keys(this.doneMap).filter(id => this.doneMap[id])
      if (!ids.length) return
      this.isSaving = true
      try {
        await Promise.all(ids.map(id =>
          this.$axios.patch(`/clinics/${this.clinicSlug}/appointments/${id}/`, { cancellation_notified: true })
        ))
        this.showMessage(['success', 'Appointment notification updated successfully'])
        this.$emit('refetch')
        this.showDialog = false
      } catch (e) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isSaving = false
      }
    },
    onWhatsapp (item) {
      if (!item.patientPhone) return
      const phone = item.patientPhone.replace(/\s+/g, '')
      window.open(`https://api.whatsapp.com/send?phone=${phone}`)
    }
  }
}
</script>
